import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Check, X, ArrowRight, Buildings, Clock, CurrencyCircleDollar, Lightning, ShieldCheck, ChartLineUp, Lock } from '@phosphor-icons/react';
import { Container, Title, Text, Button, Paper, Badge, Group, ThemeIcon, Stack } from '@mantine/core';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Icons for the strengths grid, in the same order as the `comparison.strengths`
// array in the i18n resources.
const STRENGTH_ICONS = [CurrencyCircleDollar, Clock, Lightning, ShieldCheck, Lock, ChartLineUp];

function Cell({ children, win }) {
  return (
    <td className="py-4 px-4 align-top">
      <div className="flex items-start gap-2">
        <ThemeIcon size={20} radius="xl" variant="light" color={win ? 'teal' : 'gray'} mt={1}>
          {win ? <Check size={13} weight="bold" /> : <X size={13} weight="bold" />}
        </ThemeIcon>
        <Text size="sm" c={win ? 'dark' : 'dimmed'} lh={1.45}>{children}</Text>
      </div>
    </td>
  );
}

export default function ComparisonPage() {
  const { t } = useTranslation();

  const rowsRaw = t('comparison.rows', { returnObjects: true });
  const rows = Array.isArray(rowsRaw) ? rowsRaw : [];
  const faqRaw = t('comparison.faq', { returnObjects: true });
  const faq = Array.isArray(faqRaw) ? faqRaw : [];
  const strengthsRaw = t('comparison.strengths', { returnObjects: true });
  const strengths = Array.isArray(strengthsRaw) ? strengthsRaw : [];

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO
        title={t('comparison.seo.title')}
        description={t('comparison.seo.description')}
        path="/droid-vs-agencja"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F6F5F4]">
        <Container size="lg" pt={64} pb={48}>
          <div className="max-w-3xl mx-auto text-center">
            <Badge variant="light" color="brand" size="lg" radius="xl" mb="xl">
              {t('comparison.hero.badge')}
            </Badge>
            <Title order={1} fz={{ base: 32, md: 48 }} lh={1.15} mb="md">
              {t('comparison.hero.title')}
            </Title>
            <Text size="xl" c="dimmed" lh={1.6} mb="xl">
              {t('comparison.hero.lead')}
            </Text>
            <Group justify="center" gap="md">
              <Button component={Link} to="/rejestracja" size="lg" radius="md" rightSection={<ArrowRight size={18} weight="bold" />}>
                {t('comparison.hero.ctaTry')}
              </Button>
              <Button component={Link} to="/cennik" size="lg" radius="md" variant="default">
                {t('comparison.hero.ctaPricing')}
              </Button>
            </Group>
          </div>
        </Container>
      </section>

      {/* Comparison table */}
      <section className="bg-white border-y border-gray-200">
        <Container size="lg" py={64}>
          <Title order={2} fz={{ base: 26, md: 34 }} ta="center" mb={8}>
            {t('comparison.table.title')}
          </Title>
          <Text c="dimmed" ta="center" maw={620} mx="auto" mb="xl">
            {t('comparison.table.lead')}
          </Text>

          {/* Desktop: table */}
          <div className="hidden md:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 w-1/4"></th>
                  <th className="text-left py-3 px-4">
                    <Group gap={8}>
                      <img src="/logo_icon_only.svg" alt="Droid" className="h-7 w-7" />
                      <Text fw={700} size="md">{t('comparison.table.droidName')}</Text>
                    </Group>
                  </th>
                  <th className="text-left py-3 px-4">
                    <Group gap={8}>
                      <ThemeIcon size={28} radius="md" variant="light" color="gray"><Buildings size={16} weight="fill" /></ThemeIcon>
                      <Text fw={700} size="md" c="dimmed">{t('comparison.table.agencyName')}</Text>
                    </Group>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-4 px-4 align-top">
                      <Text size="sm" fw={600}>{row.label}</Text>
                    </td>
                    <Cell win>{row.droid}</Cell>
                    <Cell win={!row.droidWins}>{row.agency}</Cell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked cards (no horizontal scroll) */}
          <div className="md:hidden space-y-3">
            {rows.map((row, i) => (
              <Paper key={i} withBorder radius="md" p="md">
                <Text size="sm" fw={700} mb="sm">{row.label}</Text>
                <Group gap={8} wrap="nowrap" align="flex-start" mb={8}>
                  <img src="/logo_icon_only.svg" alt="Droid" className="h-5 w-5 mt-[1px] shrink-0" />
                  <div>
                    <Text size="11px" c="dimmed" fw={600} tt="uppercase">{t('comparison.table.droidLabel')}</Text>
                    <Text size="sm" lh={1.4}>{row.droid}</Text>
                  </div>
                </Group>
                <Group gap={8} wrap="nowrap" align="flex-start">
                  <ThemeIcon size={20} radius="xl" variant="light" color="gray" mt={1} className="shrink-0">
                    <Buildings size={11} weight="fill" />
                  </ThemeIcon>
                  <div>
                    <Text size="11px" c="dimmed" fw={600} tt="uppercase">{t('comparison.table.agencyLabel')}</Text>
                    <Text size="sm" c="dimmed" lh={1.4}>{row.agency}</Text>
                  </div>
                </Group>
              </Paper>
            ))}
          </div>
        </Container>
      </section>

      {/* Cost section */}
      <section className="bg-[#F6F5F4]">
        <Container size="lg" py={64}>
          <div className="max-w-3xl mx-auto">
            <Title order={2} fz={{ base: 26, md: 34 }} mb="md">
              {t('comparison.cost.title')}
            </Title>
            <Text c="dimmed" lh={1.7} mb="md">
              {t('comparison.cost.p1')}
            </Text>
            <Text c="dimmed" lh={1.7} mb="xl">
              {t('comparison.cost.p2')}
            </Text>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch">
              <Paper withBorder radius="lg" p="xl" className="bg-white">
                <Group gap={8} mb="xs">
                  <img src="/logo_icon_only.svg" alt="Droid" className="h-7 w-7" />
                  <Text fw={700}>{t('comparison.cost.droidName')}</Text>
                </Group>
                <Text fz={{ base: 30, sm: 36 }} fw={800} lh={1.05}>{t('comparison.cost.droidPrice')}</Text>
                <Text size="sm" c="dimmed">{t('comparison.cost.droidPer')}</Text>
                <Text size="sm" c="dimmed" mt="md">{t('comparison.cost.droidNote')}</Text>
                <Text size="sm" fw={600} c="teal.7" mt="md">{t('comparison.cost.droidHandling')}</Text>
                <Text size="sm" c="indigo.7" mt="xs">{t('comparison.cost.droidMicro')}</Text>
              </Paper>
              <Paper withBorder radius="lg" p="xl" className="bg-white">
                <Group gap={8} mb="xs">
                  <ThemeIcon size={28} radius="md" variant="light" color="gray"><Buildings size={16} weight="fill" /></ThemeIcon>
                  <Text fw={700} c="dimmed">{t('comparison.cost.agencyName')}</Text>
                </Group>
                <Text fz={{ base: 26, sm: 32 }} fw={800} lh={1.1} c="dimmed" style={{ whiteSpace: 'nowrap' }}>{t('comparison.cost.agencyPrice')}</Text>
                <Text size="sm" c="dimmed">{t('comparison.cost.agencyPer')}</Text>
                <Text size="sm" c="dimmed" mt="md">{t('comparison.cost.agencyNote')}</Text>
                <Text size="sm" fw={600} c="dimmed" mt="md">{t('comparison.cost.agencyHandling')}</Text>
              </Paper>
            </div>
            <Text size="sm" c="dimmed" mt="md" ta="center">
              {t('comparison.cost.footnote')}
            </Text>
          </div>
        </Container>
      </section>

      {/* Strengths */}
      <section className="bg-white border-y border-gray-200">
        <Container size="lg" py={64}>
          <Title order={2} fz={{ base: 26, md: 34 }} ta="center" mb={8}>
            {t('comparison.strengthsSection.title')}
          </Title>
          <Text c="dimmed" ta="center" maw={620} mx="auto" mb="xl">
            {t('comparison.strengthsSection.lead')}
          </Text>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {strengths.map((s, i) => {
              const Icon = STRENGTH_ICONS[i] || CurrencyCircleDollar;
              return (
                <Paper key={i} withBorder radius="lg" p="lg" className="bg-[#F9FAFB]">
                  <ThemeIcon size={42} radius="md" variant="light" color="brand" mb="sm">
                    <Icon size={22} weight="duotone" />
                  </ThemeIcon>
                  <Text fw={700} mb={4}>{s.title}</Text>
                  <Text size="sm" c="dimmed" lh={1.55}>{s.desc}</Text>
                </Paper>
              );
            })}
          </div>
        </Container>
      </section>

      {/* When agency makes sense - honest section, builds trust + SEO */}
      <section className="bg-[#F6F5F4]">
        <Container size="lg" py={64}>
          <div className="max-w-3xl mx-auto">
            <Title order={2} fz={{ base: 26, md: 34 }} mb="md">
              {t('comparison.whenAgency.title')}
            </Title>
            <Text c="dimmed" lh={1.7} mb="md">
              {t('comparison.whenAgency.p1')}
            </Text>
            <Text c="dimmed" lh={1.7}>
              {t('comparison.whenAgency.p2')}
            </Text>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-gray-200">
        <Container size="md" py={64}>
          <Title order={2} fz={{ base: 26, md: 34 }} ta="center" mb="xl">
            {t('comparison.faqSection.title')}
          </Title>
          <Stack gap="md">
            {faq.map((f, i) => (
              <Paper key={i} withBorder radius="lg" p="lg" className="bg-[#F9FAFB]">
                <Text fw={700} mb={6}>{f.q}</Text>
                <Text size="sm" c="dimmed" lh={1.6}>{f.a}</Text>
              </Paper>
            ))}
          </Stack>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-[#1D4ED8]">
        <Container size="lg" py={72}>
          <div className="max-w-2xl mx-auto text-center">
            <Title order={2} c="white" fz={{ base: 28, md: 36 }} mb="md">
              {t('comparison.cta.title')}
            </Title>
            <Text c="gray.2" size="lg" mb="xl" lh={1.6}>
              {t('comparison.cta.lead')}
            </Text>
            <Button component={Link} to="/rejestracja" size="xl" radius="md"
              rightSection={<ArrowRight size={20} weight="bold" />}
              className="cta-white-btn">
              {t('comparison.cta.button')}
            </Button>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
